import { useDispatch, useSelector } from 'react-redux';
import { USER_ROLES, SuccessMessages } from 'src/common/constants';
import { variables } from 'src/Tenants';
import { downloadDocument } from 'src/helpers/DownloadDoc';
import {
  onCheckUserCanAccessTheModule,
  ToastInfo,
} from 'src/helpers/validationHelper';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  setPropertyWalkThroughFormIsVisible,
  setPropertyWalkThroughFormData,
  setPropertyWalkThroughFormStep,
  fetchConfigureTypeWalkThroughData,
} from 'src/store/actions/propertyWalkThroughForm';
import { setEasyWalkThroughFormData } from 'src/store/actions/easyWalkThroughForm';
import { fetchAreasAndSubAreas } from 'src/store/actions/areas';
import { useSuiteWalkthroughs } from 'src/pages/Suite/CustomHooks/useSuitWalkthroughs';
import {
  useWalkThroughTableUiState,
  UseWalkThroughTableUiStateReturn,
} from './useWalkThroughTableUiState';
import { useWalkthroughMutations } from './useWalkthroughMutations';
import {
  useCalendlyWalkthroughScheduling,
  UseCalendlyWalkthroughSchedulingReturn,
} from './useCalendlyWalkthroughScheduling';

const SIMPLIFIED_FAMILY: string[] = [
  'Simplified',
  'Developer Walk-through',
  'Construction Walk-through',
];
const ENHANCED_FAMILY: string[] = ['Enhanced', 'Home Owner Walk-through'];
const HOMEOWNER_OCCUPANT_ROLES: string[] = [USER_ROLES.HOME_OWNER, USER_ROLES.OCCUPANT];

export interface UseWalkThroughTableOrchestratorArgs {
  suite: any;
  property: any;
  propertyManagerDetails: any;
  refreshSuiteCounts: () => Promise<void>;
}

export interface UseWalkThroughTableOrchestratorReturn {
  user: any;
  uiState: UseWalkThroughTableUiStateReturn;

  walkthroughs: any[];
  isLoading: boolean;
  refetchWalkthroughs: () => Promise<void>;

  deleteWalkthrough: (walkthroughId: string, userId: string) => Promise<void>;

  handleStatusClicked: (data: any) => void;
  exportPDF: (data: any) => void;

  // TODO: LEGACY (Calendly backwards-compat) — suite-walkthrough self-scheduling. Remove with the hook.
  calendlyScheduling: UseCalendlyWalkthroughSchedulingReturn;
}

export const useWalkThroughTableOrchestrator = ({
  suite,
  property,
  propertyManagerDetails,
  refreshSuiteCounts,
}: UseWalkThroughTableOrchestratorArgs): UseWalkThroughTableOrchestratorReturn => {
  const dispatch = useDispatch();
  const user: any = useSelector(selectAuthenticatedUser);
  const roleName: string = user?.roles?.[0]?.role?.name;

  const uiState: UseWalkThroughTableUiStateReturn = useWalkThroughTableUiState();

  const {
    walkthroughs,
    isLoading,
    refetch: refetchWalkthroughs,
  } = useSuiteWalkthroughs(suite?.id, roleName);

  const { deleteWalkthrough, setWalkthroughStatus } = useWalkthroughMutations({
    refetchWalkthroughs,
    refreshSuiteCounts,
  });

  const calendlyScheduling: UseCalendlyWalkthroughSchedulingReturn =
    useCalendlyWalkthroughScheduling({
      suite,
      user,
      roleName,
      propertyManagerDetails,
      setWalkthroughStatus,
    });

  // TODO: legacy BOS UI logic phase 2 - migrate propertyWalkThroughForm + easyWalkThroughForm slices
  // off Redux. The dispatches inside this method feed cousin trees mounted at
  // Suite.tsx (PropertyWalkThroughForm subtree, ConfigureSubArea, EasyOccupant
  // forms). Tracked separately — wide blast radius outside this PR.
  // Asana: https://app.asana.com/1/41213529744312/project/1208712340140532/task/1214631846805248?focus=true
  const handleStatusClicked = (data: any): void => {
    if (data.status === 'Review' && roleName === 'Realtor') {
      // Legacy: realtor mobile app overlay placeholder. No-op until ported.
      return;
    }

    if (roleName === 'Realtor') {
      return;
    }

    if (!suite?.areas?.length) {
      alert(
        `No Areas/Rooms found for the sutie, Please Add Areas/Rooms to this ${variables.suite} !`
      );
      return;
    }

    const isHomeownerOrOccupant: boolean = onCheckUserCanAccessTheModule(HOMEOWNER_OCCUPANT_ROLES);

    const isSimplifiedFamily: boolean = SIMPLIFIED_FAMILY.includes(
      data.walkthroughType
    );

    const isEnhancedFamily: boolean = ENHANCED_FAMILY.includes(
      data.walkthroughType
    );

    // Developer/Admin self-fill PM identity into the form; everyone else uses
    // the suite's assigned property manager.
    const isPmSelfFill: boolean =
      roleName === 'Property Developer' || roleName === 'Admin';

    const easyFormParticipants: any = {
      homeOwnerFirstName:
        suite?.homeOwners?.firstname || suite?.firstName || '',
      homeOwnerLastName: suite?.homeOwners?.lastname || suite?.lastName || '',
      propertyManagerFirstName: isPmSelfFill
        ? user.firstname
        : property.propertyManager?.firstname || '',
      propertyManagerLastName: isPmSelfFill
        ? user.lastname
        : property.propertyManager?.lastname || '',
    };

    const walkthroughFormContext: any = {
      walkthroughType: data.walkthroughType,
      propertyId: property.id,
      propertyManagerId: property.propertyManagerId,
      propertyName: property.name,
      suiteId: suite.id,
      suiteName: variables.Suite + ' ' + suite.number,
      assetWarrantyDefaultStartDate:
        suite.assetWarrantyDefaultStartDate || null,
      status: data.status,
      walkthroughId: data.id || '',
      propertyAddress: property.addressLine1,
      propertyCity: property.city,
      propertyProvince: property.province,
      proeprtyPostalCode: property.postalCode,
      isHomeOwner: !!(suite.homeOwners && suite.homeOwners.id),
    };

    const familyFormMeta: any = {
      permissions: data.permissions || null,
      walkthroughName: data.walkthroughName,
    };

    if (data.status !== 'Activate') {
      dispatch(setPropertyWalkThroughFormData(walkthroughFormContext));
    }

    if (isSimplifiedFamily) {
      dispatch(setPropertyWalkThroughFormData(familyFormMeta));

      dispatch(setEasyWalkThroughFormData(easyFormParticipants));

      const isScheduleOrScheduledBegin: boolean =
        data.status === 'Schedule' ||
        data.status === 'Awaiting Scheduling' ||
        (data.status === 'Begin' && !!data?.schedule);

      if (isScheduleOrScheduledBegin) {
        if (isHomeownerOrOccupant) {
          calendlyScheduling.openScheduleOverlay(data);
        }

        return;
      }

      if (data.status === 'Review') {
        dispatch(setPropertyWalkThroughFormIsVisible(true));
        dispatch(setPropertyWalkThroughFormStep(5));
        dispatch(
          setEasyWalkThroughFormData({
            reviewWalkThroughServiceRequestListLoading: true,
            walkThroughId: data.id,
          })
        );
        return;
      }

      dispatch(setPropertyWalkThroughFormIsVisible(true));
      dispatch(setPropertyWalkThroughFormStep(4));
      return;
    }

    if (isEnhancedFamily) {
      dispatch(setPropertyWalkThroughFormData(familyFormMeta));

      if (data.status === null || data.status === 'Configure') {
        if (isHomeownerOrOccupant) {
          return;
        }

        dispatch(fetchAreasAndSubAreas(data.id));
        dispatch(setPropertyWalkThroughFormIsVisible(true));
        dispatch(setPropertyWalkThroughFormStep(0));
        dispatch(fetchConfigureTypeWalkThroughData(data.id, true));
        return;
      }

      if (data.status === 'In Progress') {
        if (isHomeownerOrOccupant) {
          return;
        }

        dispatch(fetchAreasAndSubAreas(data.id));
        dispatch(setPropertyWalkThroughFormIsVisible(true));
        dispatch(setPropertyWalkThroughFormStep(1));
        dispatch(setEasyWalkThroughFormData(easyFormParticipants));
        dispatch(fetchConfigureTypeWalkThroughData(data.id, true));
        return;
      }

      const isScheduleOrScheduledBegin: boolean =
        data.status === 'Schedule' ||
        data.status === 'Awaiting Scheduling' ||
        (data.status === 'Begin' && !!data?.schedule);

      if (isScheduleOrScheduledBegin) {
        if (isHomeownerOrOccupant) {
          calendlyScheduling.openScheduleOverlay(data);
        }

        return;
      }

      if (data.status === 'Review') {
        dispatch(setPropertyWalkThroughFormIsVisible(true));
        dispatch(setPropertyWalkThroughFormStep(3));
        dispatch(
          setEasyWalkThroughFormData({
            reviewWalkThroughServiceRequestListLoading: true,
            walkThroughId: data.id,
          })
        );
        return;
      }

      dispatch(setPropertyWalkThroughFormIsVisible(true));
      dispatch(setPropertyWalkThroughFormStep(2));
    }
  };

  const exportPDF = (data: any): void => {
    if (!data?.pdfurl) {
      return;
    }

    ToastInfo(SuccessMessages.pdfDownloadNotification);
    downloadDocument(data.pdfurl);
  };

  return {
    user,
    uiState,

    walkthroughs,
    isLoading,
    refetchWalkthroughs,

    deleteWalkthrough,

    handleStatusClicked,
    exportPDF,

    calendlyScheduling,
  };
};
